// My Experiement Atom Command
import { atom } from 'jotai'

import { chatRoomsAtom, messagesAtomFamily, userMapAtom, unreadCountsAtomFamily, lastSyncTimestampsAtom } from './AtomChats'
import { connectionStateAtom } from './AtomChats'
import { selectedChatRoomIdAtom } from '../../contexts/AtomUtil'
import { transformServerMessage, transformServerMessages } from '../../utils/chatUtils'
import { currentUserAtom } from '../user/AtomUsers'

/**
 * Command atom to handle receiving messages from WebSocket
 * Usage: set(receiveMessageCommandAtom, serverMessage)
 *
 * @param {Object} serverMessage - The message received from WebSocket
 *
 * This command handles:
 * - read_receipt events: Updates unread counts based on read receipts
 * - message events: Adds new messages to the messages atom and updates chat rooms
 */
export const receiveMessageCommandAtom = atom(
  null, // read function (not used for commands)
  (get, set, serverMessage) => {
    if (!serverMessage) return

    // Get current user and user ID from atom
    const currentUser = get(currentUserAtom)
    const currentUserId = currentUser?.resourceId
    if (!currentUserId) return

    // Get unreadCounts atom for current user
    const unreadCountsAtom = unreadCountsAtomFamily(currentUserId)

    // Get current state
    const chatRooms = get(chatRoomsAtom)
    const userMap = get(userMapAtom)
    const selectedRoomId = get(selectedChatRoomIdAtom)
    const unreadCounts = get(unreadCountsAtom)
    const lastSyncTimestamps = get(lastSyncTimestampsAtom)

    // Handle read receipt events
    if (serverMessage.type === 'read_receipt') {
      const { roomId, userId, timestamp } = serverMessage

      // If this is our own read receipt (from another device)
      if (userId === currentUserId) {
        // Recalculate unread count for this room based on timestamp
        const roomMessagesAtom = messagesAtomFamily(roomId)
        const roomMessages = get(roomMessagesAtom) || []
        const unreadCount = roomMessages.filter((msg) => msg.timestamp > timestamp && msg.sender.id !== currentUserId).length

        // Update conversation unread count
        set(
          chatRoomsAtom,
          chatRooms.map((conv) => (conv.id === roomId ? { ...conv, unreadCount } : conv)),
        )

        // Update unreadCounts atom
        if (unreadCount === 0) {
          const newCounts = { ...unreadCounts }
          delete newCounts[roomId]
          set(unreadCountsAtom, newCounts)
        } else {
          set(unreadCountsAtom, {
            ...unreadCounts,
            [roomId]: unreadCount,
          })
        }
      }
      return
    }

    // Handle resync events (messages fetched via WebSocket resync)
    if (serverMessage.type === 'resync') {
      const { roomId, messages: resyncMessages } = serverMessage
      if (!roomId || !Array.isArray(resyncMessages)) return

      // Transform resync messages
      const transformedMessages = transformServerMessages(resyncMessages, currentUserId, userMap)

      if (transformedMessages.length > 0) {
        // Merge with existing messages (deduplicate by id)
        const roomMessagesAtom = messagesAtomFamily(roomId)
        const existingMessages = get(roomMessagesAtom) || []
        const messageMap = new Map()

        // Add existing messages first
        existingMessages.forEach((msg) => {
          messageMap.set(msg.id, msg)
        })

        // Add resync messages (will overwrite duplicates if any)
        transformedMessages.forEach((msg) => {
          messageMap.set(msg.id, msg)
        })

        // Convert back to array and sort by timestamp (oldest-first)
        const mergedMessages = Array.from(messageMap.values()).sort((a, b) => a.timestamp - b.timestamp)

        // Update messages atom for this room
        set(roomMessagesAtom, mergedMessages)

        // Update last sync timestamp (use the newest message timestamp)
        const newestTimestamp = Math.max(...mergedMessages.map((msg) => msg.timestamp))
        set(lastSyncTimestampsAtom, {
          ...lastSyncTimestamps,
          [roomId]: newestTimestamp,
        })

        // Update conversation's last message if needed
        const newestMessage = mergedMessages[mergedMessages.length - 1]
        if (newestMessage) {
          set(
            chatRoomsAtom,
            chatRooms.map((conv) => {
              if (conv.id === roomId) {
                return {
                  ...conv,
                  lastMessage: newestMessage,
                  lastMessageTime: newestMessage.timestamp,
                }
              }
              return conv
            }),
          )
        }
      }
      return
    }

    // Handle regular message events
    if (serverMessage.type === 'message') {
      const roomId = serverMessage.roomId || serverMessage.message?.roomId
      if (!roomId) return

      // Get the actual message object
      const messageData = serverMessage.message || serverMessage

      // Transform server message to component format
      const senderInfo = userMap[messageData.senderId] || null
      const transformedMessage = transformServerMessage(messageData, currentUserId, senderInfo)

      if (transformedMessage) {
        // Get room messages atom
        const roomMessagesAtom = messagesAtomFamily(roomId)
        const roomMessages = get(roomMessagesAtom) || []

        // Check if message already exists (avoid duplicates)
        const exists = roomMessages.some((msg) => msg.id === transformedMessage.id)

        if (!exists) {
          // Add message to messages atom for this room
          set(roomMessagesAtom, [...roomMessages, transformedMessage])

          // Update last sync timestamp
          set(lastSyncTimestampsAtom, {
            ...lastSyncTimestamps,
            [roomId]: transformedMessage.timestamp,
          })

          // Check if message is from current user
          const isOwnMessage = messageData.senderId === currentUserId

          // Update conversation's last message and unread count
          set(
            chatRoomsAtom,
            chatRooms.map((conv) => {
              if (conv.id === roomId) {
                // Increment unread count if:
                // 1. Message is not from current user
                // 2. Room is not currently selected
                const shouldIncrementUnread = !isOwnMessage && selectedRoomId !== roomId
                const newUnreadCount = shouldIncrementUnread ? (conv.unreadCount || 0) + 1 : conv.unreadCount || 0

                // Update unreadCounts atom if incrementing
                if (shouldIncrementUnread) {
                  set(unreadCountsAtom, {
                    ...unreadCounts,
                    [roomId]: newUnreadCount,
                  })
                }

                return {
                  ...conv,
                  lastMessage: transformedMessage,
                  lastMessageTime: transformedMessage.timestamp,
                  unreadCount: newUnreadCount,
                }
              }
              return conv
            }),
          )
        }
      }
    }
  },
)

export const connectionStateCommandAtom = atom(
  null, // read function (not used for commands)
  (get, set, state) => {
    set(connectionStateAtom, state)
  },
)

/**
 * Command atom to initialize chat data (rooms, user map, unread counts)
 * Usage: set(initializeChatCommandAtom, { rooms, userMap, currentUserId })
 *
 * @param {Object} payload - Initialization data
 * @param {Array} payload.rooms - Array of transformed room objects
 * @param {Object} payload.userMap - Map of userId -> userInfo
 * @param {string} payload.currentUserId - Current user ID
 *
 * This command handles:
 * - Updating chatRoomsAtom with transformed rooms
 * - Updating userMapAtom with user information
 * - Updating unreadCountsAtomFamily with unread counts from server
 */
export const initializeChatCommandAtom = atom(
  null, // read function (not used for commands)
  (get, set, payload) => {
    if (!payload) return

    const { rooms, userMap, currentUserId } = payload

    if (!currentUserId) return

    // Get unreadCounts atom for current user
    const unreadCountsAtom = unreadCountsAtomFamily(currentUserId)

    // Update chat rooms
    if (Array.isArray(rooms)) {
      set(chatRoomsAtom, rooms)
    }

    // Update user map
    if (userMap && typeof userMap === 'object') {
      set(userMapAtom, userMap)
    }

    // Update unreadCounts atom with server data
    const newUnreadCounts = {}
    if (Array.isArray(rooms)) {
      rooms.forEach((room) => {
        if (room.unreadCount > 0) {
          newUnreadCounts[room.id] = room.unreadCount
        }
      })
    }
    set(unreadCountsAtom, newUnreadCounts)

    // Update lastSyncTimestamps if rooms have lastMessage
    // This helps track when we last saw messages for each room
    const currentLastSyncTimestamps = get(lastSyncTimestampsAtom)
    const updatedTimestamps = { ...currentLastSyncTimestamps }

    if (Array.isArray(rooms)) {
      rooms.forEach((room) => {
        if (room.lastMessage?.timestamp) {
          // Only update if we don't have a timestamp or if server timestamp is newer
          const currentTimestamp = updatedTimestamps[room.id] || 0
          if (room.lastMessage.timestamp > currentTimestamp) {
            updatedTimestamps[room.id] = room.lastMessage.timestamp
          }
        }
      })
      set(lastSyncTimestampsAtom, updatedTimestamps)
    }
  },
)
